import { Header } from "@app/shared/components/BackgroundedHeader";
import { LocaleSelect } from "@app/shared/components/Locale/LocaleSelect";
import { type SupportedLocales } from "@app/shared/utils/locales";
import { getRemainingWeeks } from "@app/usecases/DashboardUseCases";
import { Badge } from "@design-system/DataDisplay/Badge";
import { Gauge } from "@design-system/DataDisplay/Gauge";
import { Icon } from "@design-system/Icon";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import chroma from "chroma-js";
import { type FC } from "react";
import { type Stats } from "../../types";
import { useSupplierQuestionnaireHeaderTranslations } from "./i18n";

type QuestionnaireHeaderProps = {
    title: string;
    stats: Stats;
    locale: {
        availableLocales: SupportedLocales[];
        currentLocale: SupportedLocales;
        onLocaleChange: (locale: SupportedLocales) => void;
    };
    isQuestionnaireOpen: boolean;
    deadline?: Date;
};

export const QuestionnaireHeader: FC<QuestionnaireHeaderProps> = ({
    title,
    stats,
    locale,
    isQuestionnaireOpen,
    deadline,
}) => {
    const remainingWeeks = deadline
        ? getRemainingWeeks(new Date(deadline))
        : undefined;

    const showDeadlineBadge = isQuestionnaireOpen && remainingWeeks;

    const { t, keys } = useSupplierQuestionnaireHeaderTranslations();

    return (
        <Header bgColor="#FAFAFA" className="border border-tertiary rounded-xl">
            <Header.LeftColumn className="pl-6">
                <FlexCol gap="2" h="full" pt="2" pb="6">
                    <LocaleSelect
                        value={locale.currentLocale}
                        onChange={locale.onLocaleChange}
                        options={locale.availableLocales}
                        triggerButtonProps={{
                            variant: "text",
                        }}
                    />
                    <FlexCol gap="2" pl="2">
                        <Header.Title color="primary">{title}</Header.Title>
                        <Header.Description color="primary">
                            {t(keys.description_default)}
                        </Header.Description>
                    </FlexCol>
                </FlexCol>
            </Header.LeftColumn>
            <Header.RightColumn>
                <FlexCol h="full" w="full" justifyContent="center" p="5">
                    <FlexRow alignItems="center" justifyContent="between">
                        <Text color="primary" variant="header2">
                            {stats.validated.percentage}%
                        </Text>
                        {showDeadlineBadge ? (
                            remainingWeeks ? (
                                <Badge
                                    content={t(keys.deadline_remaining_weeks, {
                                        count: remainingWeeks,
                                    })}
                                    variant={
                                        remainingWeeks > 0 ? "success" : "error"
                                    }
                                />
                            ) : (
                                <Badge
                                    content={t(keys.deadline_overdue)}
                                    variant="error"
                                />
                            )
                        ) : null}
                    </FlexRow>
                    <Gauge>
                        <Gauge.List>
                            <Gauge.Item
                                value={stats.answered.percentage}
                                color={chroma.hex("#34C780")}
                                label={t(keys.stats_answered, {
                                    count: stats.answered.count,
                                })}
                            />
                            <Gauge.Item
                                value={stats.validated.percentage}
                                color={chroma.hex("#03A365")}
                                label={t(keys.stats_validated, {
                                    count: stats.validated.count,
                                })}
                            />
                            <Gauge.Item
                                value={
                                    100 -
                                    stats.answered.percentage -
                                    stats.validated.percentage
                                }
                                color={chroma.hex("#E6E6E6")}
                                label={t(keys.stats_not_answered, {
                                    count:
                                        stats.total.count -
                                        stats.answered.count,
                                })}
                            />
                        </Gauge.List>
                    </Gauge>
                    <FlexRow justifyContent="between">
                        <FlexRow alignItems="center" gap="2">
                            <FlexRow alignItems="center" gap="0.5">
                                <Icon name="tagCircle" color="#34C780" />
                                <Text color="primary">
                                    {t(keys.stats_answered, {
                                        count: stats.answered.count,
                                    })}
                                </Text>
                            </FlexRow>
                            <FlexRow alignItems="center" gap="0.5">
                                <Icon name="tagCircle" color="#03A365" />
                                <Text color="primary">
                                    {t(keys.stats_validated, {
                                        count: stats.validated.count,
                                    })}
                                </Text>
                            </FlexRow>
                        </FlexRow>
                        <Text color="primary">
                            {t(keys.stats_total, {
                                count: stats.total.count,
                            })}
                        </Text>
                    </FlexRow>
                </FlexCol>
            </Header.RightColumn>
        </Header>
    );
};
